import React from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, Route, Switch, useLocation } from 'react-router-dom';

import { getConfig } from '@klpkonsern/appconfig';
import { useGetByggQuery } from '@klp/bygg';
import { ByggRoutes } from '@klp/bygg/config';
import { ClientRoutes } from '@klp/client/config';
import { ContractManagementRoutes } from '@klp/contractmanagement';
import { AppRoutes } from '@klp/core/app/config';
import { Block } from '@klp/design';
import { Link } from '@klp/design/Link';
import { FakturaRoutes } from '@klp/faktura/config';
import { KontraktRoutes } from '@klp/levendekontrakter';
import { MessageBoardRoutes } from '@klp/messageboard/config';
import { useGetMessageQuery } from '@klp/messageboard/rtkApi';
import { SakRoutes } from '@klp/sak/config';
import { useGetSakQuery } from '@klp/sak/rtkApi';
import { SupportFormRoutes } from '@klp/supportapplicationform/config';
import { UserRoutes } from '@klp/user/config';

import { Crumb } from './Crumb';

const subpageRoutes = [
    { route: MessageBoardRoutes.list(), text: 'breadcrumbs.message' },
    { route: ClientRoutes.clientSelect(), text: 'breadcrumbs.selectClient' },
    { route: UserRoutes.list(), text: 'breadcrumbs.listUsers' },
    { route: UserRoutes.search(), text: 'breadcrumbs.searchUsers' },
    { route: UserRoutes.register(), text: 'breadcrumbs.registerUser' },
    {
        route: SupportFormRoutes.form(),
        text: 'breadcrumbs.supportApplicationForm',
    },
    { route: SakRoutes.list(), text: 'breadcrumbs.ticket' },
];

export const Breadcrumbs: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();

    const byggMatch = matchPath<{ byggNummer: string }>(location.pathname, {
        path: ByggRoutes.single(':byggNummer', ':tab'),
        exact: false,
        strict: false,
    });
    const byggNummer = byggMatch?.params.byggNummer;

    const { data: bygg } = useGetByggQuery(
        { byggNummer: byggNummer! },
        { skip: !byggNummer },
    );

    const sakMatch = matchPath<{ sakId: string }>(location.pathname, {
        path: SakRoutes.single(':sakId'),
        exact: false,
        strict: false,
    });
    const sakId = sakMatch?.params.sakId;

    const shouldFetchSak = sakId && sakId !== 'add';

    const { data: sakData } = useGetSakQuery(sakId!, {
        skip: !shouldFetchSak,
    });
    const sak = sakData?.sak;

    const messageMatch = matchPath<{ messageId: string }>(location.pathname, {
        path: MessageBoardRoutes.single(':messageId'),
        exact: false,
        strict: false,
    });
    const messageId = messageMatch?.params.messageId;
    const messageIdNumber = messageId ? parseInt(messageId, 10) : undefined;

    const { data: messageData } = useGetMessageQuery(
        { id: messageIdNumber! },
        { skip: !messageIdNumber },
    );
    const message = messageData?.message;

    const appRoot = getConfig('WEBAPP_APP_NAME', '');

    return (
        <>
            <Crumb>
                <Block klpTypographyVariant="small">
                    <Link href={AppRoutes.mainPage()}>
                        {t(`app.${appRoot}`)}
                    </Link>
                </Block>
            </Crumb>

            <Switch>
                {subpageRoutes.map(({ route, text }) => (
                    <Route path={route} key={route}>
                        <Crumb>
                            <Link href={route}>
                                <Block klpTypographyVariant="small">
                                    {t(text)}
                                </Block>
                            </Link>
                        </Crumb>
                    </Route>
                ))}
            </Switch>

            <Switch>
                <Route exact={true} path={FakturaRoutes.list()}>
                    <Crumb>
                        <Link href={FakturaRoutes.list()}>
                            <Block
                                klpTypographyVariant="small"
                                translation="breadcrumbs.invoice"
                            />
                        </Link>
                    </Crumb>
                </Route>
            </Switch>

            <Switch>
                <Route path={MessageBoardRoutes.add()}>
                    <Crumb>
                        <Block
                            klpTypographyVariant="small"
                            translation="message.addMessage"
                        />
                    </Crumb>
                </Route>

                <Route
                    path={MessageBoardRoutes.single(':messageId')}
                    render={({ match }) => (
                        <Crumb>
                            <Link
                                href={MessageBoardRoutes.single(
                                    match.params.messageId,
                                )}
                                klpTypographyVariant="small"
                            >
                                <Block klpTypographyVariant="small">
                                    {message?.title || ''}
                                </Block>
                            </Link>
                        </Crumb>
                    )}
                />

                <Route exact={true} path={ByggRoutes.list()}>
                    <Crumb>
                        <Block
                            klpTypographyVariant="small"
                            translation="breadcrumbs.building"
                        />
                    </Crumb>
                </Route>

                <Route exact={true} path={ByggRoutes.listAll()}>
                    <>
                        <Crumb>
                            <Link
                                href={ByggRoutes.list()}
                                klpTypographyVariant="small"
                            >
                                <Block
                                    klpTypographyVariant="small"
                                    translation="breadcrumbs.building"
                                />
                            </Link>
                        </Crumb>
                        <Crumb>
                            <Block
                                klpTypographyVariant="small"
                                translation="bygg.showAll"
                            />
                        </Crumb>
                    </>
                </Route>

                <Route exact={true} path={ByggRoutes.utleie()}>
                    <>
                        <Crumb>
                            <Link href={ByggRoutes.list()}>
                                <Block
                                    klpTypographyVariant="small"
                                    translation="breadcrumbs.building"
                                />
                            </Link>
                        </Crumb>
                        <Crumb>
                            <Block
                                klpTypographyVariant="small"
                                translation="breadcrumbs.byggUtleie"
                            />
                        </Crumb>
                    </>
                </Route>

                <Route
                    exact={true}
                    path={ByggRoutes.single(':byggNummer')}
                    render={({ match }) => (
                        <>
                            <Crumb>
                                <Link href={ByggRoutes.list()}>
                                    <Block
                                        klpTypographyVariant="small"
                                        translation="breadcrumbs.building"
                                    />
                                </Link>
                            </Crumb>
                            <Crumb>
                                <Link
                                    href={ByggRoutes.single(
                                        match.params.byggNummer,
                                    )}
                                >
                                    <Block klpTypographyVariant="small">
                                        {bygg?.navn || ''}
                                    </Block>
                                </Link>
                            </Crumb>
                        </>
                    )}
                />

                <Route
                    exact={true}
                    path={ByggRoutes.single(':byggNummer', ':tab')}
                    render={({ match }) => (
                        <>
                            <Crumb>
                                <Link
                                    href={ByggRoutes.list()}
                                    klpTypographyVariant="small"
                                >
                                    <Block
                                        klpTypographyVariant="small"
                                        translation="breadcrumbs.building"
                                    />
                                </Link>
                            </Crumb>
                            <Crumb>
                                <Link
                                    href={ByggRoutes.single(
                                        match.params.byggNummer,
                                    )}
                                    klpTypographyVariant="small"
                                >
                                    <Block klpTypographyVariant="small">
                                        {bygg?.navn || ''}
                                    </Block>
                                </Link>
                            </Crumb>
                        </>
                    )}
                />

                <Route path={SakRoutes.add()}>
                    <Crumb>
                        <Block
                            klpTypographyVariant="small"
                            translation="sak.addSak"
                        />
                    </Crumb>
                </Route>

                <Route path={SakRoutes.single(':sakId')}>
                    <Crumb>
                        <Block klpTypographyVariant="small">
                            {sak?.emne || ''}
                        </Block>
                    </Crumb>
                </Route>

                <Route path={KontraktRoutes.kontrakt()}>
                    <Crumb>
                        <Block
                            klpTypographyVariant="small"
                            translation="breadcrumbs.contract"
                        />
                    </Crumb>
                </Route>

                <Route path={ContractManagementRoutes.moduleRoute()}>
                    <Crumb>
                        <Block
                            klpTypographyVariant="small"
                            translation="breadcrumbs.contract"
                        />
                    </Crumb>
                </Route>
            </Switch>
        </>
    );
};
